import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import {
  clearSearch,
  resetFilters,
  resetIndividualFilter,
  saveFilters,
  storeLastSelectedSearchResult,
  preserveResults,
  loadPreservedResults,
  clearProfileSearch,
} from '../actions/searchActions';
import { refreshAccount, getExternalProfile } from '../actions/profileActions';
import { setHeaderHidden, clearErrors } from '../actions/commonActions';
import DefaultSearch from '../config/Search';
import queryString from 'query-string';
import { scrollToTop } from '../utils/helpers';
import { getGlobalNotifications } from '../actions/globalNotificationActions';
import useMixPanel from '../hooks/useMixPanel';
import { EVENT_NAMES } from '../constants/mixpanel';
import { displayUserLocation } from '../common';
import { useLocation } from 'react-router-dom';
import QuickOfferModalInitial from '../modules/QuickOfferModal/QuickOfferModalInitial';
import { postQuickOffer } from '../actions/offerActions';
import { INITIAL_PROFILE_PAGE_STATE } from '../constants/profile';
import SearchWithRQ from '../components/pages/Search/SearchWithRQ';
import SearchOfferHighlights from '../components/pages/Search/SearchOfferHighlights';
import { useSearchScroll } from 'hooks/useSearchScroll';

const SearchContainer = props => {
  const location = useLocation();
  const {
    loadFiltersFromDashboard,
    search,
    refreshGlobalNotification,
    profile,
    clearErrors,
    postQuickOffer,
    clearProfileSearchState,
  } = props;
  const { resetMixpanelData } = useMixPanel();

  const { city, country, region } = props.profile?.profile?.data || {};
  const userLocation = { city, country, region };
  const currentUserLocation = useMemo(() => displayUserLocation(userLocation, userLocation, true), [userLocation]);

  useEffect(() => {
    // clear existing profile search in redux
    clearProfileSearchState();

    handleResultsPreload();
    return () => {
      // refresh Global Notification on page unload
      refreshGlobalNotification();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSearchScroll();

  useEffect(() => {
    return () => {
      //when user left the page
      resetMixpanelData(EVENT_NAMES.SEARCH_VIEW);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResultsPreload = () => {
    if (search.lastSelectedSearch) {
      return;
    } else if (search.preservedResult && search.preservedResult.profile && search.preservedResult.profile.length > 0) {
      scrollToTop();
      props.loadPreservedResults();
    } else if (location.search) {
      scrollToTop();
      const qString = queryString.parse(location.search);

      const filterQuery = {
        ...qString,
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        distance: qString.distance ? parseInt(qString.distance) : '',
      };

      loadFiltersFromDashboard(filterQuery);
    }
  };

  const handleSearchResultCardSelect = (hashId: string) => {
    props.storeLastSelectedSearchResult(hashId);
  };

  return (
    <>
      <SearchWithRQ
        handleSearchResultCardSelect={handleSearchResultCardSelect}
        currentUserLocation={currentUserLocation}
        {...props}
      />

      <SearchOfferHighlights />

      {profile?.profile?.data?.account_type === 'Attractive' && (
        <QuickOfferModalInitial
          profile={profile}
          clearErrors={clearErrors}
          postQuickOffer={postQuickOffer}
          id={'searchQuickOffer'}
        />
      )}
    </>
  );
};

const mapStateTopProps = state => {
  return {
    profile: state.profile,
    search: {
      ...state.search,
      filters:
        !state.search.filters || Object.keys(state.search.filters).length <= 0
          ? DefaultSearch.filters
          : state.search.filters,
    },
    moreExists: Boolean(state.meta.search_current_page < state.meta.search_total_pages),
    userProfile: state.profile,
    searchLimit: state.meta.search_max_limit,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    clearProfileSearchState: () => {
      dispatch(clearProfileSearch());
    },
    refreshProfile: () => {
      // initial state
      dispatch(clearSearch());
      dispatch(refreshAccount());
    },
    resetFiltersState: filterType => {
      dispatch(resetFilters());
      dispatch(resetIndividualFilter(filterType));
      dispatch(clearSearch());
    },
    resetFilters: () => {
      dispatch(resetFilters());
      // re-run search
      dispatch(clearSearch());
    },
    resetIndividualFilter: filterType => {
      dispatch(resetIndividualFilter(filterType));
      dispatch(clearSearch());
    },
    loadFiltersFromDashboard: filters => {
      dispatch(preserveResults());
      dispatch(clearSearch());
      dispatch(resetFilters());
      dispatch(saveFilters({ formData: { ...filters, fromDashboard: true } }));
    },
    loadPreservedResults: () => {
      dispatch(loadPreservedResults());
    },
    saveFilters: filters => {
      dispatch(saveFilters({ formData: { ...filters } }));
    },
    clearSearch: saveSearch => {
      if (!saveSearch) {
        dispatch(clearSearch());
      }
    },
    setHeaderHidden: isHidden => {
      dispatch(setHeaderHidden(isHidden));
    },
    storeLastSelectedSearchResult: hashId => {
      dispatch(storeLastSelectedSearchResult({ id: hashId }));
    },
    refreshGlobalNotification: () => {
      dispatch(getGlobalNotifications());
    },
    clearErrors: () => {
      dispatch(clearErrors());
    },
    postQuickOffer: (formData: { accounts: string[]; price: number }) => {
      return dispatch(postQuickOffer(formData));
    },
    updateInitialProfileToProfilePage: profileData => {
      const { account, avatar } = profileData;
      const { data: photoData } = INITIAL_PROFILE_PAGE_STATE.photo;
      const { hash_id } = account.data;

      const createInitialProfileFormat = {
        ...INITIAL_PROFILE_PAGE_STATE,
        ...account.data,
        profile: { data: profileData },
        photo: {
          data: [
            {
              ...photoData[0],
              ...avatar.data,
              hash_id,
            },
          ],
        },
      };

      dispatch(getExternalProfile(createInitialProfileFormat));
    },
  };
};

export default connect(mapStateTopProps, mapDispatchToProps)(SearchContainer);
